import DashboardScopeToggle from "../../components/Common/DashboardScopeToggle";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthenticatedUser } from "../../helpers/auth-session";
import "./FirmOverviewDashboard.scss";

const finished = (row) => /complete|approved|closed/i.test(row.status || "");
const initial = (row) => /not.?started|new|draft/i.test(row.status || "");
const late = (row) =>
  !finished(row) &&
  row.dueDate &&
  new Date(row.dueDate) < new Date(new Date().setHours(0, 0, 0, 0));
export const engagementBucket = (row) =>
  finished(row)
    ? "Completed"
    : late(row)
    ? "Overdue"
    : initial(row)
    ? "Not Started"
    : "In Progress";
const date = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "Not scheduled";
const Icon = ({ name }) => <i className={`bx ${name}`} aria-hidden="true" />;
const Panel = ({ title, children, action }) => (
  <section className="firm-overview__panel">
    <header>
      <h2>{title}</h2>
      {action}
    </header>
    {children}
  </section>
);
const Empty = ({ children = "No items to display." }) => (
  <p className="firm-overview__empty">{children}</p>
);
const statusTone = (name) =>
  ({
    Completed: "green",
    Overdue: "red",
    "Not Started": "gray",
    "In Progress": "blue",
  }[name] || "blue");

export default function FirmOverviewDashboard({
  data,
  loading,
  error,
  onRetry,
  cycle,
  onCycleChange,
  onMyWork,
  reviewer,
  onReviewerChange,
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("All");
  const [search, setSearch] = useState("");
  const [chart, setChart] = useState("Findings");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [progressCycle, setProgressCycle] = useState("all");
  const [allDates, setAllDates] = useState(false);
  const [allActivity, setAllActivity] = useState(false);
  const workspace = data?.workspace || {};
  const allReviews = workspace.reviews || [];
  const cycles = workspace.cycles || [];
  const inCycle = (row) =>
    cycle === "all" || (row.cycleIds || []).map(String).includes(cycle);
  const reviews = allReviews.filter(
    (row) =>
      inCycle(row) &&
      (reviewer === "all" || String(row.reviewerId) === reviewer)
  );
  const reviewIds = new Set(reviews.map((row) => row.id));
  const findings = (workspace.allFindings || workspace.findings || []).filter(
    (row) => reviewIds.has(row.reviewId)
  );
  const queries = (workspace.allQueries || workspace.queries || []).filter(
    (row) => reviewIds.has(row.reviewId)
  );
  // Older responses still render review summaries until the enriched endpoint is running.
  const engagements = (
    workspace.engagements ||
    allReviews.map((row) => ({ ...row, reviewFallback: true }))
  )
    .filter(inCycle)
    .filter(
      (row) =>
        reviewer === "all" ||
        reviews.some(
          (review) =>
            (row.reviewFallback
              ? review.id
              : review.engagementId || review.id) === row.id
        )
    );
  const rows = engagements.map((row) => {
    const linked = reviews.filter(
      (review) =>
        (row.reviewFallback ? review.id : review.engagementId || review.id) ===
        row.id
    );
    const linkedIds = new Set(linked.map((review) => review.id));
    return {
      ...row,
      bucket: engagementBucket(row),
      findings: findings.filter((item) => linkedIds.has(item.reviewId)).length,
      queries: queries.filter((item) => linkedIds.has(item.reviewId)).length,
    };
  });
  const counts = Object.fromEntries(
    ["Completed", "In Progress", "Overdue", "Not Started"].map((name) => [
      name,
      rows.filter((row) => row.bucket === name).length,
    ])
  );
  const filtered = rows.filter(
    (row) =>
      (tab === "All" || row.bucket === tab) &&
      [row.name, row.client, row.industry, row.number].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(search.trim().toLowerCase())
      )
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / 8));
  const currentPage = Math.min(page, pageCount);
  const progressReviews = reviews.filter(
    (row) =>
      progressCycle === "all" ||
      row.cycleIds?.map(String).includes(progressCycle)
  );
  const completed = progressReviews.filter(finished).length;
  const percent = progressReviews.length
    ? Math.round((completed / progressReviews.length) * 100)
    : 0;
  const breakdown = [
    ["Completed", completed, "green"],
    [
      "In Progress",
      progressReviews.filter((row) => !finished(row) && !initial(row)).length,
      "blue",
    ],
    [
      "Not Started",
      progressReviews.filter((row) => !finished(row) && initial(row)).length,
      "gray",
    ],
    ["Overdue", progressReviews.filter(late).length, "red"],
  ];
  const chartItems = chart === "Findings" ? findings : queries;
  const chartRows = chartItems.filter(
    (row) => status === "all" || row.status === status
  );
  const statuses = [...new Set(chartItems.map((row) => row.status || "Open"))];
  const bars = [
    { name: "Total", value: chartRows.length },
    ...statuses
      .filter((name) => status === "all" || name === status)
      .map((name) => ({
        name,
        value: chartRows.filter((row) => (row.status || "Open") === name)
          .length,
      })),
  ];
  const activities = (workspace.activities || [])
    .filter((row) => !row.reviewId || reviewIds.has(row.reviewId))
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const dates = [
    ...engagements
      .filter((row) => !row.reviewFallback && !finished(row) && row.dueDate)
      .map((row) => ({
        ...row,
        key: `engagement-${row.id}`,
        detail: "Engagement due date",
        path: `/engagements/${row.id}/preview`,
      })),
    ...reviews
      .filter((row) => !finished(row) && row.dueDate)
      .map((row) => ({
        ...row,
        key: `review-${row.id}`,
        detail: "Inspection due date",
        path: `/inspectionreview/${row.id}`,
      })),
    ...findings
      .filter(
        (row) =>
          row.dueDate && !/closed|resolved|withdrawn/i.test(row.status || "")
      )
      .map((row) => ({
        ...row,
        key: `finding-${row.id}`,
        name: reviews.find((review) => review.id === row.reviewId)?.name,
        detail: "Finding response due",
        path: `/inspectionreview/${row.reviewId}`,
      })),
    ...queries
      .filter(
        (row) => row.dueDate && !/closed|resolved/i.test(row.status || "")
      )
      .map((row) => ({
        ...row,
        key: `query-${row.id}`,
        name: reviews.find((review) => review.id === row.reviewId)?.name,
        detail: "Query response due",
        path: `/inspectionreview/${row.reviewId}`,
      })),
  ].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  const people = [
    ...new Map(
      allReviews
        .filter((row) => row.reviewerId)
        .map((row) => [String(row.reviewerId), row.reviewer])
    ).entries(),
  ];
  const link = (label, path) => (
    <button
      type="button"
      className="firm-overview__link"
      onClick={() => navigate(path)}
    >
      {label} <Icon name="bx-right-arrow-alt" />
    </button>
  );
  const metric = [
    [
      workspace.engagements ? "Total Engagements" : "Reviews in scope",
      rows.length,
      "bx-briefcase",
      "blue",
      "In selected scope",
    ],
    [
      "Inspections",
      reviews.length,
      "bx-list-check",
      "blue",
      "Inspection reviews",
    ],
    [
      "Completed",
      reviews.filter(finished).length,
      "bx-check",
      "green",
      "Inspection reviews",
    ],
    [
      "In Progress",
      reviews.filter((row) => !finished(row) && !initial(row)).length,
      "bx-time-five",
      "amber",
      "Includes overdue work",
    ],
    [
      "Overdue",
      reviews.filter(late).length,
      "bx-error-circle",
      "red",
      "Incomplete inspection reviews",
    ],
  ];
  let stop = 0;
  const segments = Object.entries(counts)
    .filter(([, value]) => value)
    .map(([name, value]) => {
      const start = stop;
      stop += rows.length ? (value / rows.length) * 100 : 0;
      return `${
        {
          Completed: "#21b889",
          "In Progress": "#1671f8",
          Overdue: "#f65b53",
          "Not Started": "#c3cfdf",
        }[name]
      } ${start}% ${stop}%`;
    });
  return (
    <main className="firm-overview piers-page">
      <header className="firm-overview__intro piers-page-header overview-scope-header">
        <div className="firm-overview__identity">
          <span className="firm-overview__icon blue">
            <Icon name="bx-buildings" />
          </span>
          <div>
            <h1 className="piers-page-title">{getAuthenticatedUser()?.firmName || "Firm Overview"}</h1>
            <p>
              <strong>Firm Overview</strong>
              <span>
                Key information, progress and activity for all inspection cycles
                and engagements.
              </span>
            </p>
          </div>
        </div>
          <DashboardScopeToggle value="firm" onChange={onMyWork} />
        <div className="firm-overview__controls">

          <select
            aria-label="Inspection cycle"
            value={cycle}
            onChange={(event) => {
              onCycleChange(event.target.value);
              setPage(1);
              setProgressCycle("all");
            }}
          >
            <option value="all">All Cycles</option>
            {cycles.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Reviewer"
            value={reviewer}
            onChange={(event) => {
              onReviewerChange(event.target.value);
              setPage(1);
            }}
          >
            <option value="all">All reviewers</option>
            {people.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </header>
      {error ? (
        <div role="alert">
          {error}{" "}
          <button type="button" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : loading ? (
        <p role="status">Loading firm overview…</p>
      ) : (
        <>
          <section className="firm-overview__metrics" aria-label="Firm summary">
            {metric.map(([label, value, icon, tone, hint]) => (
              <article key={label}>
                <span className={`firm-overview__icon ${tone}`}>
                  <Icon name={icon} />
                </span>
                <div>
                  <strong>{value}</strong>
                  <span>{label}</span>
                  <small>{hint}</small>
                </div>
              </article>
            ))}
          </section>
          <div className="firm-overview__grid">
            <Panel title="Inspection Cycle Progress">
              <select
                aria-label="Progress inspection cycle"
                value={progressCycle}
                onChange={(event) => setProgressCycle(event.target.value)}
              >
                <option value="all">
                  {cycle === "all"
                    ? "All selected cycles"
                    : cycles.find((row) => String(row.id) === cycle)?.name}
                </option>
                {cycles
                  .filter((row) => cycle === "all" || String(row.id) === cycle)
                  .map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                      {row.startDate
                        ? ` (${date(row.startDate)} – ${date(row.endDate)})`
                        : ""}
                    </option>
                  ))}
              </select>
              <div className="firm-overview__progress">
                <progress
                  aria-label="Completed inspection reviews"
                  value={percent}
                  max="100"
                />
                <div>
                  <strong>{percent}%</strong>
                  <small>
                    {completed} of {progressReviews.length} completed
                  </small>
                </div>
              </div>
              <div className="firm-overview__breakdown">
                {breakdown.map(([name, value, tone]) => (
                  <div key={name} className={tone}>
                    <b>{value}</b>
                    <span>{name}</span>
                  </div>
                ))}
              </div>
              <small>
                Overdue reviews may also be in progress or not started.
              </small>
            </Panel>
            <Panel
              title={
                workspace.engagements ? "Engagement Status" : "Review Status"
              }
            >
              <div className="firm-overview__status">
                <div
                  className="firm-overview__donut"
                  role="img"
                  aria-label={`${rows.length} engagements: ${Object.entries(
                    counts
                  )
                    .map(([name, value]) => `${value} ${name}`)
                    .join(", ")}`}
                  style={{
                    background: segments.length
                      ? `conic-gradient(${segments.join(",")})`
                      : "#e6edf6",
                  }}
                >
                  <div>
                    <strong>{rows.length}</strong>
                    <small>Engagements</small>
                  </div>
                </div>
                <div className="firm-overview__legend">
                  {Object.entries(counts).map(([name, value]) => (
                    <div key={name}>
                      <i className={statusTone(name)} />
                      <span>{name}</span>
                      <b>
                        {value} (
                        {rows.length
                          ? Math.round((value / rows.length) * 100)
                          : 0}
                        %)
                      </b>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
            <div className="firm-overview__analytics">
              <Panel title="Findings and Queries">
                <div className="firm-overview__chart-controls">
                  <div role="group" aria-label="Findings and queries">
                    {["Findings", "Queries"].map((name) => (
                      <button
                        type="button"
                        key={name}
                        aria-pressed={chart === name}
                        onClick={() => {
                          setChart(name);
                          setStatus("all");
                        }}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                  <select
                    aria-label="Finding or query status"
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                  >
                    <option value="all">All Status</option>
                    {statuses.map((name) => (
                      <option key={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="firm-overview__bars">
                  {bars.map((row, index) => (
                    <div key={row.name}>
                      <div className="firm-overview__bar-track">
                        <span
                          style={{
                            height: `${
                              chartRows.length
                                ? (row.value / chartRows.length) * 100
                                : 0
                            }%`,
                            background: [
                              "#3185fc",
                              "#96c4ff",
                              "#ffbd3e",
                              "#2fba8c",
                              "#c3cfdf",
                            ][index % 5],
                          }}
                        />
                      </div>
                      <span>{row.name}</span>
                      <b>{row.value}</b>
                    </div>
                  ))}
                </div>
                {!chartItems.length && (
                  <Empty>No {chart.toLowerCase()} in this scope.</Empty>
                )}
              </Panel>
            </div>
            <section className="firm-overview__engagements">
              <Panel
                title={
                  workspace.engagements
                    ? "Engagements by Status"
                    : "Reviews by Status"
                }
                action={link("View all", "/engagements/list")}
              >
                <label className="firm-overview__search">
                  <Icon name="bx-search" />
                  <input
                    aria-label="Search engagements"
                    placeholder="Search engagements…"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                  />
                </label>
                <div
                  className="firm-overview__tabs"
                  role="group"
                  aria-label="Engagement status"
                >
                  {[
                    "All",
                    "Completed",
                    "In Progress",
                    "Overdue",
                    "Not Started",
                  ].map((name) => (
                    <button
                      key={name}
                      type="button"
                      aria-pressed={tab === name}
                      onClick={() => {
                        setTab(name);
                        setPage(1);
                      }}
                    >
                      {name} ({name === "All" ? rows.length : counts[name]})
                    </button>
                  ))}
                </div>
                <div className="firm-overview__table">
                  <table>
                    <thead>
                      <tr>
                        {[
                          "Engagement",
                          "Industry / Service",
                          "Engagement Status",
                          "Due Date",
                          "Findings",
                          "Queries",
                          "Action",
                        ].map((name) => (
                          <th key={name}>{name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered
                        .slice((currentPage - 1) * 8, currentPage * 8)
                        .map((row) => (
                          <tr key={row.id}>
                            <td>
                              <strong>{row.name}</strong>
                              <small>{row.client || row.number}</small>
                            </td>
                            <td>{row.industry || "Not specified"}</td>
                            <td>
                              <span
                                className={`firm-overview__pill ${statusTone(
                                  row.bucket
                                )}`}
                              >
                                {row.bucket}
                              </span>
                            </td>
                            <td
                              className={late(row) ? "firm-overview__late" : ""}
                            >
                              {date(row.dueDate)}
                            </td>
                            <td>{row.findings}</td>
                            <td>{row.queries}</td>
                            <td>
                              <button
                                type="button"
                                className="firm-overview__view"
                                onClick={() =>
                                  navigate(
                                    row.reviewFallback
                                      ? `/inspectionreview/${row.id}`
                                      : `/engagements/${row.id}/preview`
                                  )
                                }
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {!filtered.length && (
                  <Empty>No engagements match this scope or search.</Empty>
                )}
                <div className="firm-overview__pagination">
                  <small>
                    {filtered.length} engagements · Page {currentPage} of{" "}
                    {pageCount}
                  </small>
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={currentPage === pageCount}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </Panel>
            </section>
            <aside className="firm-overview__sidebar">
              <Panel
                title="Upcoming Key Dates"
                action={
                  <button
                    type="button"
                    className="firm-overview__link"
                    aria-expanded={allDates}
                    onClick={() => setAllDates(!allDates)}
                  >
                    {allDates ? "Show less" : "View all"}
                  </button>
                }
              >
                {dates.slice(0, allDates ? dates.length : 5).map((row) => (
                  <button
                    type="button"
                    className="firm-overview__date-row"
                    key={row.key}
                    onClick={() => navigate(row.path)}
                  >
                    <time className={late(row) ? "red" : ""}>
                      <small>
                        {new Date(row.dueDate).toLocaleDateString("en-GB", {
                          month: "short",
                        })}
                      </small>
                      <b>{new Date(row.dueDate).getDate()}</b>
                    </time>
                    <span>
                      <strong>{row.name || "Inspection review"}</strong>
                      <small>
                        {row.detail}
                        {late(row) ? " · Overdue" : ""}
                      </small>
                    </span>
                    <Icon name="bx-chevron-right" />
                  </button>
                ))}
                {!dates.length && <Empty>No outstanding due dates.</Empty>}
              </Panel>
              <Panel
                title="Recent Activity"
                action={
                  <button
                    type="button"
                    className="firm-overview__link"
                    aria-expanded={allActivity}
                    onClick={() => setAllActivity(!allActivity)}
                  >
                    {allActivity ? "Show less" : "View all"}
                  </button>
                }
              >
                {activities
                  .slice(0, allActivity ? activities.length : 4)
                  .map((row, index) => (
                    <div
                      className="firm-overview__activity"
                      key={`${row.date}-${index}`}
                    >
                      <span className="firm-overview__icon blue">
                        <Icon name="bx-history" />
                      </span>
                      <div>
                        <strong>{row.title}</strong>
                        <small>{row.person}</small>
                      </div>
                      <time>{row.time || date(row.date)}</time>
                    </div>
                  ))}
                {!activities.length && <Empty>No recent activity.</Empty>}
              </Panel>
            </aside>
            <details className="firm-overview__support">
              <summary>Team workload and inspection context</summary>
              <div className="firm-overview__table">
                <table>
                  <thead>
                    <tr>
                      <th>Review</th>
                      <th>Reviewer</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviews.map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        <td>{row.reviewer}</td>
                        <td>{row.status}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/inspectionreview/${row.id}`)
                            }
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!reviews.length && (
                <Empty>No reviews require attention in this scope.</Empty>
              )}
            </details>
          </div>
        </>
      )}
    </main>
  );
}
